import * as THREE from 'three';
import type { Terrain } from '@/terrain/Terrain';

/**
 * The brush cursor: two rings laid on the ground itself.
 *
 * A flat disc drawn at the pointer's altitude is the easy version and it is useless on a slope — it
 * floats over a bank or sinks into a mound, and the user has no idea what the brush will actually
 * reach. So the rings are sampled: every vertex is placed at the terrain height under it, which
 * makes the cursor drape over the ground and read as a physical footprint. On a steep site this is
 * the difference between aiming and guessing.
 *
 * Two rings, not one, because the brush has two radii that both matter. The outer is where the
 * effect ends; the inner is where the falloff begins, i.e. the region that gets the full effect. A
 * soft brush shows a wide gap between them and a hard one shows almost none, so the hardness setting
 * is legible without reading the number.
 *
 * Depth-tested, so the ring disappears behind a rise — which is exactly the feedback you want when
 * the brush is over ground you cannot see.
 */
export class TerrainBrushCursor {
  readonly object = new THREE.Group();

  private readonly outer: THREE.Line;
  private readonly inner: THREE.Line;
  private readonly segments: number;
  private readonly local = new THREE.Vector2();

  constructor(segments = 72) {
    this.segments = segments;
    this.object.name = 'terrain-brush-cursor';
    this.object.visible = false;
    this.outer = this.createRing(0.95);
    this.inner = this.createRing(0.55);
    this.object.add(this.outer, this.inner);
  }

  private createRing(opacity: number): THREE.Line {
    const positions = new Float32Array((this.segments + 1) * 3);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity,
      depthWrite: false,
    });
    const line = new THREE.Line(geometry, material);
    line.name = 'terrain-brush-ring';
    line.frustumCulled = false;
    return line;
  }

  setVisible(visible: boolean): void {
    this.object.visible = visible;
  }

  setColor(color: THREE.ColorRepresentation): void {
    (this.outer.material as THREE.LineBasicMaterial).color.set(color);
    (this.inner.material as THREE.LineBasicMaterial).color.set(color);
  }

  /**
   * Re-lay both rings around a terrain-local centre.
   *
   * `hardness` places the inner ring; a hardness of 0 (fully feathered) collapses it toward the
   * centre rather than hiding it, so there is always something marking where the peak of the brush
   * is when the outer ring is large and faint.
   */
  update(terrain: Terrain, centerX: number, centerZ: number, radius: number, hardness: number): void {
    this.layRing(this.outer, terrain, centerX, centerZ, radius);
    this.layRing(this.inner, terrain, centerX, centerZ, radius * Math.max(0.06, hardness));
  }

  private layRing(
    line: THREE.Line,
    terrain: Terrain,
    centerX: number,
    centerZ: number,
    radius: number,
  ): void {
    const attribute = line.geometry.getAttribute('position') as THREE.BufferAttribute;
    const positions = attribute.array as Float32Array;
    for (let i = 0; i <= this.segments; i += 1) {
      const angle = (i / this.segments) * Math.PI * 2;
      const localX = centerX + Math.cos(angle) * radius;
      const localZ = centerZ + Math.sin(angle) * radius;
      // Height comes from the local grid; the point itself has to go back out to world space,
      // because the cursor hangs off the overlay root rather than off the rotated terrain mesh.
      const height = terrain.sampleHeight(localX, localZ);
      terrain.toWorld(localX, localZ, this.local);
      positions[i * 3] = this.local.x;
      positions[i * 3 + 1] = height + 0.06;
      positions[i * 3 + 2] = this.local.y;
    }
    attribute.needsUpdate = true;
  }

  dispose(): void {
    for (const line of [this.outer, this.inner]) {
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    }
    this.object.clear();
    this.object.removeFromParent();
  }
}
